import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment-timezone";
import "moment/locale/tr";

import { Loading } from "../../utils/InlineComponents";
import { AppContext } from "../../HomePage";
import { UserService } from "../../service/UserService";
import i18n from "../../i18n";
import { getMonthsAgo, toDateString } from "../../utils/TimeUtil";
import { getKeyValue, user } from "../../utils/Utils";
import { Calendar } from "primereact/calendar";
import JsonToExcel from "../Export/JsonToExcel";

const AVATAR_COLORS = ['#185FA5', '#534AB7', '#0F6E56', '#BA7517', '#993C1D', '#72243E'];
const colorFor = (email) => {
    let h = 0;
    for (let i = 0; i < (email || "").length; i++) h = (h * 31 + email.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initials = (email) => {
    const local = ((email || "").split("@")[0]) || (email || "");
    const letters = local.replace(/[^a-zA-Z]/g, "");
    return (letters.substring(0, 2) || (email || "?").substring(0, 1)).toUpperCase();
};

const SignInLogs = (props) => {
    const { t, setError } = React.useContext(AppContext);

    const [dates, setDates] = useState([getMonthsAgo(1), new Date()]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});

    const userService = new UserService();
    const u = user();
    const ownerEmail = (u && u.email) || "";
    const tz = (u && u.timezone) || null;
    const lang = (i18n.language || 'en').split('-')[0];

    useEffect(() => { search(); /* eslint-disable-next-line */ }, [dates[0], dates[1]]);

    const search = () => {
        if (dates[0] && dates[1]) {
            setLoading(true);
            userService.getTokens(toDateString(dates[0]), toDateString(dates[1])).then(data => {
                setItems(data || []);
                setLoading(false);
            }).catch(error => { setError(error); setError(null); setLoading(false); });
        }
    };

    const m = (d) => tz ? moment(d).tz(tz) : moment(d);
    const fromNow = (d) => moment(d).locale(lang).fromNow();

    const groups = () => {
        const map = {};
        (items || []).forEach(it => { (map[it.user_email] = map[it.user_email] || []).push(it); });
        const list = Object.keys(map).map(email => {
            const events = map[email].slice().sort((a, b) => new Date(b.created) - new Date(a.created));
            return { email, events, count: events.length, last: events[0] ? events[0].created : null };
        });
        list.sort((a, b) => {
            if (a.email === ownerEmail) return -1;
            if (b.email === ownerEmail) return 1;
            return (b.last ? new Date(b.last).getTime() : 0) - (a.last ? new Date(a.last).getTime() : 0);
        });
        return list;
    };

    const byDay = (events) => {
        const nowM = m(new Date());
        const todayKey = nowM.format('YYYY-MM-DD');
        const yKey = nowM.clone().subtract(1, 'day').format('YYYY-MM-DD');
        const out = [];
        const idx = {};
        events.forEach(ev => {
            const key = m(ev.created).format('YYYY-MM-DD');
            const label = key === todayKey ? t('account.signin.today', 'Today')
                : key === yKey ? t('account.signin.yesterday', 'Yesterday')
                : m(ev.created).locale(lang).format('LL');
            if (idx[label] === undefined) { idx[label] = out.length; out.push({ label, events: [] }); }
            out[idx[label]].events.push(ev);
        });
        return out;
    };

    const isOpen = (email) => (expanded[email] !== undefined ? expanded[email] : email === ownerEmail);
    const toggle = (email) => setExpanded(prev => ({ ...prev, [email]: !isOpen(email) }));

    const g = groups();
    const totalSignins = (items || []).length;
    const userCount = g.length;
    const lastActivity = g.reduce((mx, x) => {
        if (!x.last) return mx;
        if (!mx) return x.last;
        return new Date(x.last) > new Date(mx) ? x.last : mx;
    }, null);

    return loading ?
        <div style={{ textAlign: 'center' }}><Loading /></div> :
        <div className="acc-section">
            <p className="acc-section-title">{t('account.headers.sign_in_logs', 'Sign-in logs')}</p>
            <p className="acc-section-sub">{t('account.signin.sub', 'Recent sign-ins for your account and team.')}</p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <Calendar id="dates" readOnlyInput selectionMode="range" numberOfMonths={2} value={dates}
                          onChange={(e) => setDates(getKeyValue(e).value)} showIcon dateFormat="yy-mm-dd" showButtonBar />
                <JsonToExcel data={items ? items.map(i => ({ email: i.user_email, login_time: i.created })) : null} fileName="SignInLogs" />
            </div>

            <div className="acc-sl-stats">
                <div className="acc-sl-stat"><div className="v">{totalSignins}</div><div className="l">{t('account.signin.stat_signins', 'sign-ins')}</div></div>
                <div className="acc-sl-stat"><div className="v">{userCount}</div><div className="l">{t('account.signin.stat_users', 'users')}</div></div>
                <div className="acc-sl-stat"><div className="v">{lastActivity ? fromNow(lastActivity) : '—'}</div><div className="l">{t('account.signin.stat_last', 'last activity')}</div></div>
            </div>

            {g.length === 0 && <div className="acc-empty">{t('actions.empty_message', 'No records')}</div>}

            {g.map(usr => (
                <div className="acc-sl-card" key={usr.email}>
                    <button type="button" className="acc-sl-head" onClick={() => toggle(usr.email)}>
                        <span className="acc-sl-av" style={{ background: colorFor(usr.email) }}>{initials(usr.email)}</span>
                        <span className="acc-sl-id">
                            <span className="acc-sl-name">{usr.email === ownerEmail ? t('account.signin.you', 'You') : usr.email}</span>
                            <span className="acc-sl-mail">{usr.email === ownerEmail ? usr.email : t('account.signin.team_member', 'Team member')}</span>
                        </span>
                        <span className="acc-sl-meta">
                            <span className="acc-sl-count">{usr.count} {t('account.signin.signins_label', 'sign-ins')}</span>
                            <span>{usr.last ? fromNow(usr.last) : ''}</span>
                            <i className={"pi " + (isOpen(usr.email) ? "pi-chevron-down" : "pi-chevron-right")} />
                        </span>
                    </button>
                    {isOpen(usr.email) &&
                        <div className="acc-sl-body">
                            {byDay(usr.events).map(day => (
                                <React.Fragment key={day.label}>
                                    <div className="acc-sl-day">{day.label}</div>
                                    <div className="acc-sl-chips">
                                        {day.events.map((ev, i) => (
                                            <span className="acc-sl-chip" key={i}>
                                                <i className="pi pi-sign-in" />
                                                {m(ev.created).format('HH:mm')}
                                                <span className="ago">· {fromNow(ev.created)}</span>
                                            </span>
                                        ))}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>}
                </div>
            ))}
        </div>;
};
export default SignInLogs;
